<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>商品管理</h1>
    <div class="header-actions">
      <button class="btn-export" @click="exportReport">匯出報表</button>
      <button class="btn-primary" @click="openAddProductModal">
        + 新增商品
      </button>
    </div>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input type="text" v-model="searchQuery" placeholder="搜尋商品名稱..." />
    </div>

    <div class="filter-buttons">
      <!-- 篩選商品狀態 -->
      <select v-model="filterStatus" class="filter-select">
        <option value="">全部商品</option>
        <option value="active">上架中</option>
        <option value="inactive">下架中</option>
        <option value="lowStock">庫存低</option>
        <option value="outOfStock">缺貨中</option>
      </select>
    </div>
  </div>

  <!-- 商品列表區塊 -->
  <div class="product-list">
    <div
      v-for="product in paginatedProducts"
      :key="product.id"
      class="product-card"
    >
      <!-- 商品圖片 -->
      <div class="product-image">
        <img
          :src="product.imageUrl"
          :alt="product.name"
          class="product-img"
          @error="handleImageError($event, product)"
        />
        <span v-if="product.meter">
          <span v-if="product.stock === 0" class="badge badge-danger"
            >缺貨中</span
          >
          <span v-else-if="product.stock <= 10" class="badge badge-warning"
            >庫存低</span
          >
          <span v-else-if="product.active" class="badge badge-success"
            >上架中</span
          >
          <span v-else class="badge badge-secondary">下架中</span>
        </span>
      </div>

      <!-- 商品資訊 -->
      <div class="product-info">
        <h3 class="product-name">{{ product.name }}</h3>
        <p class="product-price">
          售價 <strong>NT$ {{ product.price.toLocaleString() }}</strong>
        </p>
        <p class="product-stock">
          <template v-if="product.meter"> 庫存：{{ product.stock }} </template>
          <template v-else>
            <span v-if="product.stock === 0" class="text-danger">缺貨中</span>
            <span v-else class="text-success">有庫存</span>
          </template>
        </p>
        <p class="product-date">
          上架日期：{{ formatDate(product.createdDate) }}
        </p>
      </div>

      <!-- 商品操作 -->
      <div class="product-actions product-actions-desktop">
        <!-- 規格管理 -->
        <button
          class="btn-icon"
          title="規格管理"
          @click="openSpecModal(product)"
        >
          <img src="@/assets/image/icon/rectangle-list.png" class="all-icon" />
        </button>

        <!-- 編輯 -->
        <button class="btn-icon" title="編輯" @click="openEditModal(product)">
          <img src="@/assets/image/icon/edit.png" class="all-icon" />
        </button>

        <!-- 刪除 -->
        <button
          class="btn-icon"
          title="刪除"
          @click="deleteProduct(product.id)"
        >
          <img src="@/assets/image/icon/trash.png" class="all-icon" />
        </button>
      </div>

      <!-- 手機/平板版文字按鈕 -->
      <div class="product-actions-mobile">
        <button
          class="btn-mobile-product-action"
          @click="openSpecModal(product)"
        >
          規格管理
        </button>

        <button
          class="btn-mobile-product-action"
          @click="openEditModal(product)"
        >
          編輯
        </button>

        <button
          class="btn-mobile-product-action delete"
          @click="deleteProduct(product.id)"
        >
          刪除
        </button>
      </div>
    </div>
  </div>

  <!-- ✅ 手機版分頁 -->
  <div class="pagination-container mobile-pagination">
    <button
      class="pagination-btn"
      @click="prevPage"
      :disabled="currentPage === 1"
    >
      ◀ 上一頁
    </button>
    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>
    <button
      class="pagination-btn"
      @click="nextPage"
      :disabled="currentPage >= totalPages"
    >
      下一頁 ▶
    </button>
  </div>

  <!-- ✅ 電腦版分頁 (共用樣式) -->
  <div class="pagination-container desktop-pagination">
    <button
      class="pagination-btn"
      @click="prevPage"
      :disabled="currentPage === 1"
    >
      ◀ 上一頁
    </button>
    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>
    <button
      class="pagination-btn"
      @click="nextPage"
      :disabled="currentPage >= totalPages"
    >
      下一頁 ▶
    </button>
  </div>

  <!-- 新增/編輯商品 Modal -->
  <div v-if="showProductModal" class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ editingProduct ? "編輯商品" : "新增商品" }}</h2>
        <button class="close-btn" @click="closeProductModal">×</button>
      </div>

      <div class="modal-body">
        <!-- 左側欄位 -->
        <div class="left-column">
          <!-- 產品名稱 -->
          <div class="form-group">
            <label class="form-label">產品名稱 *</label>
            <input
              type="text"
              class="form-input"
              v-model="formData.name"
              placeholder="請輸入產品名稱"
            />
          </div>

          <!-- 價格 -->
          <div class="form-group">
            <label class="form-label">價格 *</label>
            <input
              type="number"
              class="form-input"
              v-model.number="formData.price"
              placeholder="請輸入價格"
            />
          </div>

          <!-- 成本價格 -->
          <div v-if="canViewCostPrice" class="form-group">
            <label class="form-label">成本價格</label>
            <input
              type="number"
              class="form-input"
              v-model.number="formData.costPrice"
              placeholder="請輸入成本價格"
            />
          </div>

          <!-- 分類 -->
          <div class="form-group">
            <label class="form-label">分類</label>
            <select class="form-input" v-model.number="formData.subCategoryId">
              <option :value="0">請選擇分類</option>
              <option v-for="sub in categories" :key="sub.id" :value="sub.id">
                {{ sub.name }} {{ sub.isActive ? "(啟用)" : "(停用)" }}
              </option>
            </select>
          </div>

          <!-- 標籤管理 -->
          <div class="form-group">
            <div class="form-label-row">
              <label class="form-label">標籤 (Tags)</label>
              <small class="hint-text">最多 3 個</small>
            </div>
            <div class="tag-input-wrapper">
              <div
                class="tag-list"
                v-if="formData.tags && formData.tags.length > 0"
              >
                <span
                  v-for="(tag, index) in formData.tags"
                  :key="index"
                  class="tag-item"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="remove-tag"
                    @click="removeTag(index)"
                  >
                    ×
                  </button>
                </span>
              </div>
              <!-- 輸入框 + 加入按鈕 -->
              <div class="tag-input-row">
                <input
                  type="text"
                  class="form-input"
                  v-model="newTag"
                  @input="limitTagLength"
                  @keyup.enter="addTag"
                  @keyup="handleTagInput"
                  placeholder="輸入標籤後按 Enter 新增"
                  :disabled="formData.tags.length >= 3"
                />
                <!-- ✅ 新增：加入標籤按鈕（手機友善） -->
                <button
                  type="button"
                  class="btn btn-add-tag"
                  @click="addTag"
                  :disabled="formData.tags.length >= 3"
                >
                  加入
                </button>
              </div>
            </div>
          </div>

          <!-- 影片連結 -->
          <div class="form-group">
            <label class="form-label">影片連結</label>
            <input
              type="text"
              class="form-input"
              v-model="formData.videoUrl"
              placeholder="請輸入影片連結"
            />
          </div>

          <!-- 商品狀態 -->
          <div class="form-group">
            <label class="form-label">商品狀態</label>
            <div class="status-toggle">
              <label class="toggle-switch">
                <input type="checkbox" v-model="formData.active" />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">
                {{ formData.active ? "上架中" : "已下架" }}
              </span>
            </div>
          </div>

          <!-- 描述 -->
          <div class="form-group">
            <label class="form-label">商品描述</label>
            <textarea
              class="form-input"
              v-model="formData.description"
              placeholder="請輸入商品描述"
              rows="4"
              maxlength="200"
            ></textarea>
            <div class="char-count">
              {{ formData.description?.length || 0 }} / 200 字
            </div>
          </div>
        </div>

        <!-- 右側欄位 -->
        <div class="right-column">
          <!-- 藥品圖片 -->
          <div class="form-group">
            <label class="form-label">商品圖片</label>
            <div class="image-upload-area" @click="$refs.imageInput.click()">
              <input
                type="file"
                ref="imageInput"
                accept="image/jpeg,image/png,image/jpg"
                @change="handleImageUpload"
                style="display: none"
              />

              <div v-if="formData.imagePreview" class="image-preview">
                <img
                  :src="
                    formData.imagePreview || require('@/assets/image/AYH.png')
                  "
                  alt="商品預覽圖"
                  class="preview-img"
                  @error="handleImageError($event)"
                />
                <button
                  type="button"
                  class="remove-image-btn"
                  @click.stop="removeImage"
                >
                  ✕ 更換圖片
                </button>
              </div>

              <div v-else class="upload-placeholder">
                <span class="upload-icon">📷</span>
                <p class="upload-text">點擊上傳商品圖片</p>
                <p class="upload-hint">支援 JPG、PNG 格式</p>
              </div>
            </div>

            <div class="image-requirements">
              <p class="requirement-title">📋 圖片規範</p>
              <ul class="requirement-list">
                <li>格式：JPG、JPEG、PNG</li>
                <li>尺寸：建議 1600x900 像素</li>
                <li>大小：不超過 2MB</li>
              </ul>
            </div>
          </div>

          <!-- 庫存管理 -->
          <div class="form-group">
            <label class="form-label">庫存管理</label>

            <!-- Toggle Switch -->
            <div class="stock-toggle">
              <label class="toggle-switch">
                <input type="checkbox" v-model="formData.trackStock" />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">開啟庫存追蹤</span>
            </div>

            <!-- 當開啟庫存計算時顯示 -->
            <div v-if="formData.trackStock" class="stock-content">
              <label class="form-label">庫存數量 *</label>
              <input
                type="number"
                class="form-input"
                v-model.number="formData.stock"
                placeholder="請輸入庫存數量"
                min="0"
              />
            </div>

            <!-- ✅ 當「未開啟庫存追蹤」時，顯示有／無庫存單選 -->
            <div v-else class="stock-status-group">
              <label class="form-label">庫存狀態</label>
              <div class="radio-group">
                <label class="radio-option">
                  <input
                    type="radio"
                    name="stockStatus"
                    value="inStock"
                    v-model="formData.stockStatus"
                  />
                  <span class="radio-label">有庫存</span>
                </label>
                <label class="radio-option">
                  <input
                    type="radio"
                    name="stockStatus"
                    value="noStock"
                    v-model="formData.stockStatus"
                  />
                  <span class="radio-label">無庫存</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-cancel" @click="closeProductModal">取消</button>
        <button class="btn btn-submit" @click="saveProduct">
          {{
            editingProduct
              ? "儲存變更"
              : isSpecificationStep
              ? "新增商品"
              : "下一步"
          }}
        </button>
      </div>
    </div>
  </div>

  <FormatView
    :visible="showFormatModal"
    :initialData="selectedProduct"
    :isNewProductFlow="isSpecificationStep"
    :userRole="userRole"
    @close="showFormatModal = false"
    @confirm="submitVariants"
    @back="handleBackToProductEdit"
  />
</template>

<script src="@/assets/js/product.js"></script>
<style scoped src="@/assets/css/product.css"></style>
